import { PlanTrailActionContext } from "./types";
import { Route } from "../../../../../utils/models/Route";
import { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import { MarkerFeature, MarkerGeometry, MarkerProperties } from "../../../../../utils/models/MarkerFeature";
import { snapPoints } from "../../../../../utils/models/Graphooper";
import { hiikerElevation } from "../../../../../utils/helpers/HiikerElevationApiHelper";
import { generateID } from "../../../../../utils/helpers/IDGenerator";
import { nearestOnTrail, pointAtDistance, ON_TRAIL_M, LockedBoundary, routeAnchorCoord, dayNumberForDistance } from "../../../utils/masterTrail";
import { buildLockedDayRoute, buildLockedSegmentLineString, FrozenTrail } from "../../../services/locked-boundaries";
import { haversineMeters } from "../../../services/stage-connectors";
import { bridgeCoordinate } from "../../../services/off-network-bridge";
import { StageDestination } from "../../../services/stage-destinations";
import { MoveLockedBoundaryCommand, LockedRouteChange } from "../../undo/commands/MoveLockedBoundaryCommand";
import { MoveLockedMarkerCommand } from "../../undo/commands/MoveLockedMarkerCommand";

const ON_TRAIL_BOUNDARY = (distanceM: number): LockedBoundary => ({ distanceM, offTrailPoint: null, detourLine: null });

/**
 * Nearest point on the frozen trail to `targetCoord`, clamped to
 * [minDist, maxDist], plus an off-trail detour leg to the exact drop point
 * when it's further than ON_TRAIL_M from that gate. Shared by boundary drags
 * (clamped between neighboring boundaries) and interior marker drags
 * (clamped between the moved marker's immediate neighbors).
 *
 * `forceOffTrail` bypasses the ON_TRAIL_M heuristic. That heuristic exists to
 * treat a near-miss drop as "the user meant the trail", but an explicit
 * attach to a POI/waypoint/highlight is a deliberate choice about a specific
 * place — for an item within 30m of the path (a hotel just off it, say) the
 * heuristic would otherwise swallow the attach and silently leave the
 * boundary on the trail beside it.
 */
async function gateOntoTrail(
  trail: FrozenTrail,
  targetCoord: number[],
  minDist: number,
  maxDist: number,
  isSnap: boolean,
  routePreference: string,
  forceOffTrail: boolean = false
): Promise<LockedBoundary> {
  const gate = nearestOnTrail(trail.coordinates, trail.cumDist, targetCoord[0], targetCoord[1]);
  const clampedDist = Math.min(Math.max(gate.distanceM, minDist), maxDist);
  const gatePoint = clampedDist === gate.distanceM
    ? gate.point
    : pointAtDistance(trail.coordinates, trail.cumDist, clampedDist);

  const offTrailM = haversineMeters(targetCoord, gatePoint);
  let offTrailPoint: number[] | null = null;
  let detourLine: LineStringFeature | null = null;

  // `offTrailM > 0` still guards the forced case: an item sitting exactly on
  // the trail needs no detour, and a zero-length one would be degenerate.
  if (offTrailM > ON_TRAIL_M || (forceOffTrail && offTrailM > 0)) {
    offTrailPoint = [...targetCoord];
    const gateMarker = new MarkerFeature(
      new MarkerGeometry([...gatePoint]),
      new MarkerProperties(generateID(), isSnap, Date.now().toString(), gatePoint[2] ?? 0)
    );
    const targetMarker = new MarkerFeature(
      new MarkerGeometry([...targetCoord]),
      new MarkerProperties(generateID(), isSnap, Date.now().toString(), 0)
    );
    detourLine = isSnap
      ? await snapPoints(gateMarker, targetMarker, routePreference).catch(async (err) => {
          // GraphHopper genuinely has no route to the drop point (e.g. a POI
          // off any routable way) — fall back to a straight detour rather
          // than letting this reject uncaught, which would abort the drag
          // mid-interaction and leave the boundary/marker stuck.
          console.error('snapPoints failed while gating onto trail, falling back to a straight line:', err);
          return hiikerElevation(gateMarker, targetMarker);
        })
      : await hiikerElevation(gateMarker, targetMarker);
    detourLine.lineStringProperties.id = generateID();

    // GraphHopper ends a routed detour on the nearest routable WAY, which for
    // an item set back from the path (a hotel entrance, say) can be a good
    // 15m short of the item itself. That matters for an explicit attach: the
    // day's end marker comes from this line's last coordinate, and
    // resolveAttachedDestinations prunes any destination more than
    // DESTINATION_ATTACHED_THRESHOLD_M (15m) from its item — so a detour that
    // stops short gets the attachment deleted again the moment
    // updateStageConnectorsOnMap runs. Bridge the remainder to the item, the
    // same way polylines.ts extends an unreachable pinned endpoint.
    if (forceOffTrail) {
      const coords = detourLine.lineStringGeometry.coordinates;
      const last = coords[coords.length - 1];
      if (!last || haversineMeters(last, targetCoord) > 0) {
        // Elevation inherited from the routed point this joins onto — the item
        // has none of its own, and a 0 would read as phantom descent.
        coords.push(bridgeCoordinate(targetCoord[0], targetCoord[1], targetCoord[2], last));
      }
    }
  }

  return { distanceM: clampedDist, offTrailPoint, detourLine };
}

/**
 * Locked-mode boundary drag: the frozen master trail (state.lockedMasterTrail)
 * never moves. Dragging a stage marker repositions where day `boundaryDay`
 * ends / day `boundaryDay + 1` begins along that trail (clamped between its
 * immediate neighbors), and — if dropped off-trail — adds a routed detour to
 * the drop point, folded into the affected day route(s)' own geometry. Also
 * regroups any alt/spur leg on one of those two days onto whichever one it
 * now actually sits closest to (see the affectedDays block below), so a drag
 * can't leave a variant labelled under a day whose route no longer runs
 * anywhere near it. See store/modules/actions/drag-preview.ts
 * (finalizeConnectorDrag) and store/modules/actions/stage-connectors.ts
 * (floatingUp) for the callers.
 */
export const lockedBoundaryDragActions = {
  async applyLockedBoundaryDrag(
    { state, commit }: PlanTrailActionContext,
    payload: {
      boundaryDay: number;
      prevRouteKey: string | null;
      nextRouteKey: string | null;
      targetCoord: [number, number];
      /**
       * Set when the drop was an attach to a POI/waypoint/highlight. The
       * frozen trail still decides where the day is SLICED — so guided mode
       * keeps its "never accidentally cut the hike short" guarantee — while
       * the day's route detours off-trail to end on the item, and the item's
       * identity is recorded so it persists as the day's end_item_*.
       */
      destination?: StageDestination | null;
    }
  ): Promise<boolean> {
    const trail = state.lockedMasterTrail;
    if (!trail) return false;

    const { boundaryDay, prevRouteKey, nextRouteKey, targetCoord, destination } = payload;
    const total = trail.cumDist[trail.cumDist.length - 1] ?? 0;

    const prevBoundary = state.lockedBoundaries[boundaryDay - 1] ?? ON_TRAIL_BOUNDARY(0);
    const nextBoundary = state.lockedBoundaries[boundaryDay + 1] ?? ON_TRAIL_BOUNDARY(total);

    // Nearest point on the frozen trail, clamped so this boundary can never
    // cross its immediate neighbors.
    const boundaryAfter = await gateOntoTrail(
      trail, targetCoord, prevBoundary.distanceM, nextBoundary.distanceM, state.isSnap, state.routePreference,
      destination != null
    );

    const boundaryBefore: LockedBoundary = JSON.parse(JSON.stringify(
      state.lockedBoundaries[boundaryDay] ?? ON_TRAIL_BOUNDARY(boundaryAfter.distanceM)
    ));

    state.lockedBoundaries = { ...state.lockedBoundaries, [boundaryDay]: boundaryAfter };

    const routeChanges: LockedRouteChange[] = [];

    // prevRouteKey is null for the trail's absolute start (boundaryDay 0) —
    // there's no route before it to rebuild, only nextRoute (day 1) moves.
    const prevRoute = prevRouteKey ? state.routes[prevRouteKey] : null;
    if (prevRoute && prevRouteKey) {
      const before: Route = JSON.parse(JSON.stringify(prevRoute));
      const { markers, lineStrings } = buildLockedDayRoute(trail, prevBoundary, boundaryAfter);
      prevRoute.markers = markers;
      prevRoute.lineStrings = lineStrings;
      prevRoute.updated_at = new Date();
      routeChanges.push({ routeKey: prevRouteKey, before, after: JSON.parse(JSON.stringify(prevRoute)) });
    }

    if (nextRouteKey) {
      const nextRoute = state.routes[nextRouteKey];
      if (nextRoute) {
        const before: Route = JSON.parse(JSON.stringify(nextRoute));
        const { markers, lineStrings } = buildLockedDayRoute(trail, boundaryAfter, nextBoundary);
        nextRoute.markers = markers;
        nextRoute.lineStrings = lineStrings;
        nextRoute.updated_at = new Date();
        routeChanges.push({ routeKey: nextRouteKey, before, after: JSON.parse(JSON.stringify(nextRoute)) });
      }
    }

    // Only day `boundaryDay` and `boundaryDay + 1`'s extents just changed —
    // regroup any alt/spur leg currently labelled with one of those two days
    // onto whichever one its own geometry (unchanged) now actually sits
    // closest to, the same way a day-count re-slice does (guided-route.ts's
    // applyDayCount). Transport legs are excluded — they can run between two
    // off-trail points with no reliable on-trail anchor to project from.
    const dayCount = state.guidedDayCount;
    if (dayCount) {
      const affectedDays = new Set([boundaryDay, boundaryDay + 1]);
      Object.values(state.routes).forEach((route: Route) => {
        if (route.route_style !== 'alt' && route.route_style !== 'spur') return;
        if (!affectedDays.has(route.day ?? 1)) return;

        const anchor = routeAnchorCoord(route);
        if (!anchor) return;
        const nearest = nearestOnTrail(trail.coordinates, trail.cumDist, anchor[0], anchor[1]);
        const newDay = dayNumberForDistance(nearest.distanceM, state.lockedBoundaries, dayCount);
        if (newDay === (route.day ?? 1)) return;

        const before: Route = JSON.parse(JSON.stringify(route));
        route.day = newDay;
        route.updated_at = new Date();
        routeChanges.push({ routeKey: route.id, before, after: JSON.parse(JSON.stringify(route)) });
      });
    }

    state.undoManager.push(new MoveLockedBoundaryCommand({
      dayNumber: boundaryDay,
      boundaryBefore,
      boundaryAfter,
      routeChanges,
    }));

    // Record the pin AFTER the rebuild, so the coordinates stored are the
    // item's own — buildLockedDayRoute has just ended the day's geometry
    // there via the detour, which is what keeps this inside
    // DESTINATION_ATTACHED_THRESHOLD_M and stops resolveAttachedDestinations
    // pruning it as stale.
    if (destination) {
      commit('setStageDestination', {
        dayNumber: boundaryDay,
        destination: { ...destination, coordinates: [targetCoord[0], targetCoord[1]] },
      });
    }

    state._statsVersion++;
    return true;
  },

  /**
   * Locked-mode drag of a marker that ISN'T a day boundary — a genuine
   * interior control point, or an endpoint with no adjacent day (a
   * single-day plan, or a non-main-line route, which was never part of the
   * derived master trail). The marker can still be dropped anywhere
   * (including a deliberate off-trail excursion), but the one or two line
   * segments touching it always resolve back onto the exact recorded trail
   * rather than a fresh routing-engine guess that could shortcut and skip
   * part of the original hike — the same guarantee applyLockedBoundaryDrag
   * gives day boundaries, generalized to any marker.
   *
   * Each neighbor's own on-trail gate is recomputed fresh from its CURRENT
   * position on every call — there's no persisted per-marker boundary slot
   * to keep in sync, so correctness never depends on stale stored state.
   *
   * Returns false (leaving the caller to fall back to a plain move) when the
   * route has no usable master-trail relationship.
   */
  async applyLockedMarkerDrag(
    { state }: PlanTrailActionContext,
    payload: { routeKey: string; markerIndex: number; targetCoord: [number, number] }
  ): Promise<boolean> {
    const trail = state.lockedMasterTrail;
    if (!trail) return false;

    const { routeKey, markerIndex, targetCoord } = payload;
    const route = state.routes[routeKey];
    const marker = route?.markers[markerIndex];
    if (!route || !marker) return false;

    // Alt/spur/transport legs were never part of the derived master trail —
    // leave them to the normal free-form reroute the caller falls back to.
    if ((route.route_style ?? 'route') !== 'route') return false;

    const total = trail.cumDist[trail.cumDist.length - 1] ?? 0;
    const before: Route = JSON.parse(JSON.stringify(route));

    const prevNeighbor = markerIndex > 0 ? route.markers[markerIndex - 1] : null;
    const nextNeighbor = markerIndex < route.markers.length - 1 ? route.markers[markerIndex + 1] : null;

    const prevGuess = prevNeighbor
      ? nearestOnTrail(trail.coordinates, trail.cumDist,
          prevNeighbor.markerGeometry.coordinates[0], prevNeighbor.markerGeometry.coordinates[1]).distanceM
      : 0;
    const nextGuess = nextNeighbor
      ? nearestOnTrail(trail.coordinates, trail.cumDist,
          nextNeighbor.markerGeometry.coordinates[0], nextNeighbor.markerGeometry.coordinates[1]).distanceM
      : total;

    // Clamp the moved marker's own gate between its neighbors' gates so a
    // rebuilt segment's slice can never run backwards along the trail.
    const movedGate = await gateOntoTrail(
      trail, targetCoord, Math.min(prevGuess, nextGuess), Math.max(prevGuess, nextGuess), state.isSnap, state.routePreference
    );

    if (prevNeighbor) {
      const prevGate = await gateOntoTrail(
        trail, prevNeighbor.markerGeometry.coordinates, 0, total, state.isSnap, state.routePreference
      );
      route.lineStrings[markerIndex - 1] = buildLockedSegmentLineString(trail, prevGate, movedGate);
    }
    if (nextNeighbor) {
      const nextGate = await gateOntoTrail(
        trail, nextNeighbor.markerGeometry.coordinates, 0, total, state.isSnap, state.routePreference
      );
      route.lineStrings[markerIndex] = buildLockedSegmentLineString(trail, movedGate, nextGate);
    }

    marker.markerGeometry.coordinates = movedGate.offTrailPoint
      ? [...movedGate.offTrailPoint]
      : pointAtDistance(trail.coordinates, trail.cumDist, movedGate.distanceM);
    route.updated_at = new Date();

    state.undoManager.push(new MoveLockedMarkerCommand({
      routeKey,
      before,
      after: JSON.parse(JSON.stringify(route)),
    }));
    state._statsVersion++;
    return true;
  },
};
